import { useEffect, useState } from "react";
import {
  exemptionHints,
  exemptionTargetOf,
  findExemptionCode,
  loadExemptionList,
} from "./exemptionSearch";
import { looksLikeExemptionCode, nearestExemptionCodes } from "./exemptionSearch";
import { anchorTargetProps, appUrl, spaClick, useLinkTarget } from "./reqLinks";

// 검색어가 감면부호인가 — "pending"(부호 꼴인데 목록을 아직 못 받음) | true | false.
// 부호 검색은 물품이 아니므로 결정트리 사전 점검·제안(LLM)·웹 의미 넓히기를 돌리지 않는다.
export function useExemptionCodeQuery(q) {
  const shaped = looksLikeExemptionCode(q);
  const [state, setState] = useState(shaped ? "pending" : false);
  useEffect(() => {
    if (!shaped) {
      setState(false);
      return undefined;
    }
    let alive = true;
    setState("pending");
    loadExemptionList().then((d) => {
      // 목록에 있는 부호거나, 앞자리가 같은 부호가 있는 "부호 꼴" 검색이면 물품 검색을 돌리지 않는다.
      if (alive) setState(!!findExemptionCode(q, d.codes) || nearestExemptionCodes(q, d.codes).length > 0);
    });
    return () => {
      alive = false;
    };
  }, [q, shaped]);
  return state;
}

// 통합 검색 결과 위 한 줄 — 검색어가 관세감면 조문 낱말이면 그 조문으로 가는 링크(문의 #76).
// "대사관" → 제88조 외교관용물품 등의 면세. 세번 검색 결과는 건드리지 않는다. 없으면 아무것도 그리지 않는다.
export default function ExemptionHintLine({ q }) {
  const [articles, setArticles] = useState([]);
  const [codes, setCodes] = useState([]);
  const target = useLinkTarget();

  useEffect(() => {
    let alive = true;
    loadExemptionList().then((d) => {
      if (!alive) return;
      setArticles(d.items || []);
      setCodes(d.codes || []);
    });
    return () => {
      alive = false;
    };
  }, []);

  // 감면부호를 그대로 넣은 검색("A088000101") — 부호 내용·감면율과 그 조문으로 가는 링크.
  const code = findExemptionCode(q, codes);
  if (code) {
    const url = appUrl({ audit: "exemption", ...exemptionTargetOf(code, articles), eq: code.code });
    const article = articles.find((a) => a.article === code.art);
    return (
      <div
        data-testid="exemption-code-line"
        style={{
          fontSize: 13,
          margin: "0 0 10px",
          padding: "9px 12px",
          borderLeft: "3px solid var(--audit-accent)",
          background: "var(--audit-bg-subtle)",
          lineHeight: 1.7,
        }}
      >
        <div>
          <span style={{ fontWeight: 700 }}>{code.tax ? `${code.tax} 감면·면세 부호` : "관세감면 부호"}</span>{" "}
          <span style={{ fontFamily: "var(--audit-mono, monospace)", fontWeight: 800 }}>{code.code}</span>
          {code.rate != null && <span> · 감면율 {code.rate}%</span>}
          {code.post_mgmt && <span> · 사후관리 대상</span>}
        </div>
        <div>{code.content}</div>
        <a
          href={url}
          onClick={spaClick(url)}
          {...anchorTargetProps(target)}
          style={{ color: "var(--audit-accent)", textDecoration: "none", fontWeight: 600 }}
        >
          {article ? `제${article.article}조 ${article.title}` : code.law} 의 감면부호 전체 보기 →
        </a>
      </div>
    );
  }

  // 부호 꼴인데 보유 부호표에 없다 — 물품으로 읽지 않고, 앞자리가 같은 부호를 보인다.
  const nearest = nearestExemptionCodes(q, codes);
  if (nearest.length > 0) {
    return (
      <div
        data-testid="exemption-code-missing"
        style={{
          fontSize: 13,
          margin: "0 0 10px",
          padding: "9px 12px",
          borderLeft: "3px solid var(--audit-border-strong)",
          background: "var(--audit-bg-subtle)",
          lineHeight: 1.7,
        }}
      >
        <div>
          <span style={{ fontWeight: 700 }}>감면부호 형식</span>{" "}
          <span style={{ fontFamily: "var(--audit-mono, monospace)", fontWeight: 800 }}>
            {String(q).trim().toUpperCase()}
          </span>
          <span style={{ color: "var(--audit-text-muted)" }}> — 보유한 부호표에 없습니다</span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {nearest.map((c) => {
            const url = appUrl({ audit: "exemption", ...exemptionTargetOf(c, articles), eq: c.code });
            return (
              <a
                key={c.code}
                href={url}
                onClick={spaClick(url)}
                {...anchorTargetProps(target)}
                style={{ color: "var(--audit-accent)", textDecoration: "none" }}
                title={c.content}
              >
                {c.code} →
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  const hints = exemptionHints(q, articles);
  if (hints.length === 0) return null;
  return (
    <div
      data-testid="exemption-hint-line"
      style={{
        fontSize: 13,
        margin: "0 0 10px",
        padding: "7px 10px",
        borderLeft: "3px solid var(--audit-accent)",
        background: "var(--audit-bg-subtle)",
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "baseline",
      }}
    >
      <span style={{ fontWeight: 700 }}>관세감면 항목</span>
      {hints.map((a) => {
        const url = appUrl({ audit: "exemption", art: String(a.article), eq: String(q || "").trim() });
        return (
          <a
            key={a.article}
            href={url}
            onClick={spaClick(url)}
            {...anchorTargetProps(target)}
            style={{ color: "var(--audit-accent)", textDecoration: "none", fontWeight: 600 }}
          >
            제{a.article}조 {a.title} →
          </a>
        );
      })}
      <span style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>감면부호 · 감면율 확인</span>
    </div>
  );
}
